import classnames from 'classnames';
import type {
  ButtonHTMLAttributes,
  ComponentType,
  MouseEvent,
  ReactNode,
  Ref,
} from 'react';
import type { To } from 'react-router';
import styled, { css, keyframes } from 'styled-components';

import type { LinkProps } from '@sorare/routing';
import { Link } from '@sorare/routing';

import { BaseLoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { useClickHandler } from '#src/hooks/useClickHandler.ts';
import { useLoadingForMethod } from '#src/hooks/useLoadingForMethod.ts';

import type { colors } from './variants.ts';

export type Color = (typeof colors)[number];

export interface Props extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
  'type' | 'onClick'
> {
  size: 'compact' | 'small' | 'medium' | 'large';
  color?: Color;
  to?: To | number;
  href?: string;
  replace?: boolean;
  fullWidth?: boolean;
  disableDebounce?: boolean;
  externalLink?: boolean;
  state?: { [key: string]: any };
  className?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  type?: 'submit' | 'button';
  asSpan?: boolean;
  blur?: boolean;
  prefetch?: LinkProps<typeof Link>['prefetch'];
  ref?: Ref<HTMLElement>;
  onClick?: (
    e: MouseEvent<HTMLButtonElement>
  ) => void | Promise<void> | Promise<unknown>;
}

const Label = styled.span``;

const zoomIn = keyframes`
  to {
    transform: scale(1)
  }
`;

const hoverAfterStyle = css`
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
`;

const style = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--unit);
  height: 48px;
  padding: 0px 20px;
  box-shadow: none;
  border: none;
  border-radius: var(--unit);
  position: relative;
  & {
    letter-spacing: 0;
    white-space: nowrap;
    font: var(--t-label-l);
    font-weight: var(--t-bold);
  }
  transition: background-color 0.2s ease-in-out;

  &.fullWidth {
    width: 100%;
  }
  &.compact {
    padding: 0 var(--unit);
    height: var(--triple-unit);
    font: var(--t-label-m);
    font-weight: var(--t-bold);
  }
  &.small {
    padding: 0 var(--double-unit);
    height: var(--quadruple-unit);
  }
  &.medium {
    height: 40px;
    /* ensures a nice rounding button when there is a small text */
    min-width: 40px;
    /* max padding before going higher than 40 if there is only 1char */
    padding: 0px 12px;
  }
  &.primary {
    background-color: var(--c-blue-400);
    color: var(--c-white);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nd-50);
      }
    }
  }
  &.secondary {
    background-color: var(--c-white);
    color: var(--c-black);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nl-100);
      }
    }
  }
  &.tertiary {
    background-color: var(--c-black);
    border: 1px solid var(--c-nd-150);
    color: var(--c-white);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nd-50);
      }
    }
  }
  &.quaternary {
    background-color: var(--c-nd-50);
    color: var(--c-white);

    &.active,
    &:hover,
    &:focus {
      background: var(--c-nd-100);
    }
  }
  &.transparent {
    color: var(--c-white);
    &:hover {
      background: var(--c-nd-50);
    }

    &.disabled,
    &:disabled {
      &,
      &:active,
      &:hover,
      &:focus {
        background: none;
      }
    }
  }
  &.red {
    background-color: var(--c-surface-error);
    color: var(--c-red-400);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nd-50);
      }
    }
  }
  &.green {
    background-color: var(--c-surface-success);
    color: var(--c-onBase-success);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nd-50);
      }
    }
  }
  &.yellow {
    background-color: rgba(var(--c-rgb-yellow-300), 0.16);
    color: var(--c-yellow-300);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nd-50);
      }
    }
  }
  &.purple {
    background-color: var(--c-rewards-squad);
    color: var(--c-white);

    &.active,
    &:hover,
    &:focus {
      &:after {
        ${hoverAfterStyle}
        background: var(--c-nd-50);
      }
    }
  }
  &.disabled,
  &:disabled {
    &,
    &:active,
    &:hover,
    &:focus {
      color: var(--c-nd-500);
      background: var(--c-nd-150);
      border-color: transparent;

      &:after {
        content: none;
      }
    }
  }

  &.blur {
    backdrop-filter: blur(64px);
  }
`;

type Wrapper = ComponentType<
  Omit<ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>, 'type'>
>;

const StyledButton: Wrapper = styled.button`
  ${style}
`;

const StyledLink = styled(Link)`
  ${style}
`;

const StyledA: Wrapper = styled.a`
  ${style}
`;

const LoadingIndicatorWrapper = styled.div`
  font-size: calc(3 * var(--unit));
  &.small {
    font-size: calc(2 * var(--unit));
  }
  &.compact {
    font-size: var(--unit);
  }
`;

const Loader = styled.span`
  position: absolute;
  display: flex;
  margin: auto;
  transform: scale(0);
  .loading & {
    animation: 0.2s ease-in-out ${zoomIn} both;
  }
`;

const Cta = styled.span`
  display: contents;
  .loading & > * {
    transition: transform ease-in-out 0.2s;
    transform: scale(0);
  }
`;

const wrappedChildren = (
  children: ReactNode,
  startIcon?: ReactNode,
  endIcon?: ReactNode
): ReactNode => {
  if (typeof children === 'string' && (startIcon || endIcon)) {
    return <Label>{children}</Label>;
  }
  return children;
};

export const Button = ({
  children,
  size,
  color = 'primary',
  onClick,
  to,
  href,
  disableDebounce = false,
  externalLink = false,
  className,
  startIcon,
  endIcon,
  type = 'button',
  fullWidth,
  asSpan,
  role = href ? 'link' : 'button',
  blur,
  disabled,
  prefetch,
  ref,
  ...rest
}: Props) => {
  const [isLoading, wrappedMethod] = useLoadingForMethod(onClick);
  const clickHandler = useClickHandler(wrappedMethod, disableDebounce);

  const props = {
    ref,
    onClick: clickHandler,
    ...(externalLink
      ? ({ target: '_blank', rel: 'noopener noreferrer' } as any)
      : {}),
    to,
    href,
    type,
    role,
    disabled: disabled || isLoading,
    prefetch: to ? prefetch : undefined,
    as: asSpan ? 'span' : undefined,
    ...rest,
    className: classnames(color, className, size, {
      fullWidth,
      blur,
      disabled,
      loading: isLoading,
    }),
    children: (
      <>
        {!!isLoading && (
          <Loader>
            <LoadingIndicatorWrapper className={size}>
              <BaseLoadingIndicator />
            </LoadingIndicatorWrapper>
          </Loader>
        )}
        <Cta>
          {startIcon}
          {wrappedChildren(children, startIcon, endIcon)}
          {endIcon}
        </Cta>
      </>
    ),
  };

  if (to && !disabled) {
    return <StyledLink {...props} />;
  }
  if (href) {
    return <StyledA {...props} />;
  }
  return <StyledButton {...props} />;
};
